import {useEffect, useRef, useState} from "react";
import {useIntegrator} from "../../integrator.context";
import PromptDialog from "../../elements/promptDialog.element";
import LocalizationInput from "../../elements/localizationInput.element";
import NumberInputLabel from "../../elements/numberInputLabel.element";
import {UpdateResourceCommand} from "../../commands/updateResource.command";
import {AddResourceCommand} from "../../commands/addResource.command";
import {RemoveResourceCommand} from "../../commands/removeResource.command";
import BooleanInput from "../../elements/booleanInput.element";
import MetadataDragLabel from "../../elements/metadataDragLabel";
import {ResourceData} from "../../models/remoteConstants.model";
import {DropTargetMonitor, useDrop} from "react-dnd";
import MetadataLabel from "../../elements/metadataLabel.element";
import IconButton from "../../elements/IconButton";
import ImagePackElement from "../../elements/imagePack.element";
import List from "../../elements/list";
import ResizeBar, {useResizable} from "../../elements/resizeBar";
import Collapsible from "../../elements/collapsible";
import {useApp} from "../../app.context";
import {getDuplicateIds} from "../../utils/metadata.utils";

function ResourcesEditor() {
    const {state, executeCommand} = useIntegrator();
    const {getLocalizationValue, navigateTo} = useApp();
    const [showPrompt, setShowPrompt] = useState(false);
    const dupes = getDuplicateIds(state.techActionAttributes ?? []);

    // Resizable palette: a single vertical bar drags the width. See
    // elements/resizeBar; no collapse buttons here.
    const containerRef = useRef<HTMLDivElement>(null);
    const palette = useResizable({
        storageKey: 'rift.editor.resourcesPaletteWidth',
        axis: 'x', min: 240, invert: true,                      // palette sits right of the bar
        initial: () => Math.round(window.innerWidth * 0.25),    // old fixed 25vw
        // Leave the resources list at least this wide.
        max: () => (containerRef.current?.getBoundingClientRect().width ?? Infinity) - 320,
    });

    return (
        <div ref={containerRef} className={'layout horizontal'} style={{flexGrow: 1}}>
            <div className={'layout vertical gap'} style={{gap: 10, flexGrow: 1, minWidth: 0}}>
                <List title={'Resources'} elements={state.resources}
                      headerClassName={'box'}
                      headerAction={<button style={{whiteSpace: 'nowrap'}} onClick={() => setShowPrompt(true)}>Add New Resource</button>}
                      component={element => <ResourceItem resource={element}/>}
                      search={element => getLocalizationValue(element.id)}
                      keyFor={element => element.id}
                />
                {showPrompt && <PromptDialog
                    title={'New Resource'}
                    placeholder={'Enter resource name...'}
                    existingNames={state.resources.map(r => getLocalizationValue(r.id))}
                    onConfirm={(name) => { const c = new AddResourceCommand(name); executeCommand(c); if (c.newId) navigateTo({kind: 'resource', id: c.newId}); setShowPrompt(false); }}
                    onCancel={() => setShowPrompt(false)}
                />}
            </div>

            {/* Draggable bar to resize the palette width (drags horizontally). */}
            <ResizeBar axis={'x'} {...palette.handleProps}/>

            <div className={'layout vertical gap'} style={{gap: 10, width: palette.size, flexShrink: 0}}>
                <div className={'layout vertical gap box flex-grow'}>
                    <List title={'Tech Actions'} elements={state.techActionAttributes}
                          component={element => <MetadataDragLabel uid={element.uid} id={element.id} type={'attribute'} values={element.values} duplicate={dupes.has(element.id)} draggable={() => true}/>}
                          search={element => getLocalizationValue(element.id)}
                          keyFor={element => element.uid}
                    />
                </div>
            </div>
        </div>
    )
}

function ResourceItem(props: { resource: ResourceData }) {
    const {getLocalizationValue, confirmDelete, focusedResource} = useApp();
    const {executeCommand} = useIntegrator();

    // Ctrl/Cmd-click navigation target: open + scroll this resource into view
    // when it becomes the focused resource (mirrors the unit/building rows).
    const [open, setOpen] = useState(false);
    const rowRef = useRef<HTMLDivElement | null>(null);
    useEffect(() => {
        if (focusedResource?.id === props.resource.id) {
            setOpen(true);
            rowRef.current?.scrollIntoView({behavior: 'smooth', block: 'center'});
        }
    }, [focusedResource, props.resource.id]);

    function checkCanDrop(monitor: DropTargetMonitor<any, void>): boolean {
        if (!monitor.isOver({shallow: true})) {
            return false;
        }

        return !props.resource.attributes?.includes(monitor.getItem().id);
    }

    const [{canDrop}, drop] = useDrop({
        accept: 'attribute',
        drop: (item: any, monitor) => {
            if (!checkCanDrop(monitor)) {
                return;
            }

            props.resource.attributes ??= [];
            props.resource.attributes.push(item.id)
            executeCommand(new UpdateResourceCommand(props.resource))
        },
        collect: (monitor) => {
            return {
                canDrop: checkCanDrop(monitor)
            };
        },
    });

    const header = (
        <div className={'layout horizontal'} style={{gap: 10, alignItems: 'center'}}>
            <ImagePackElement id={props.resource.id} level={0} size={80}/>
            <LocalizationInput id={props.resource.id} style={{width: 100}}/>
            <BooleanInput value={props.resource.active} onChange={(value) => {
                props.resource.active = value;
                executeCommand(new UpdateResourceCommand(props.resource));
            }}/>
            <IconButton icon={'trash'} onClick={async (e) => {
                e.stopPropagation();
                if (await confirmDelete(getLocalizationValue(props.resource.id), 'resource'))
                    executeCommand(new RemoveResourceCommand(props.resource.id));
            }}/>
        </div>
    );

    return (
        <div ref={(el) => { drop(el); rowRef.current = el; }} className={'box transition'} style={{
            opacity: props.resource.active ? 1 : 0.5,
            border: `solid 1px ${canDrop ? 'var(--tint-color)' : 'var(--overlay-color)'}`
        }}>
            <Collapsible title={header} defaultOpen={false} open={open} onOpenChange={setOpen}>
                <div className={'layout horizontal gap'} style={{gap: 20, marginTop: 10}}>
                    <div className={'layout vertical flex-grow'} style={{gap: 10}}>
                        <div className={'layout vertical gap'} style={{borderRadius: '5px', flexGrow: 1}}>
                            <p className={'text header'}>Tech Actions</p>
                            {props.resource.attributes && props.resource.attributes.map((uid, i) => {
                                return <MetadataLabel key={i} uid={uid} onDelete={() => {
                                    props.resource.attributes = props.resource.attributes.filter(r => r !== uid);
                                    executeCommand(new UpdateResourceCommand(props.resource));
                                }}/>
                            })}
                        </div>
                    </div>
                    <div className={'layout vertical gap flex-grow'} style={{gap: 20}}>
                        <p className={'text header'}>World Generation</p>
                        <div className={'layout vertical gap'}>
                            <NumberInputLabel field={'resource.rarity'} value={props.resource.rarity}
                                              onChange={(value) => {
                                                  props.resource.rarity = value;
                                                  executeCommand(new UpdateResourceCommand(props.resource));
                                              }}/>
                        </div>
                        <p className={'text header'}>Statistics</p>
                        <div className={'layout vertical gap'}>
                            <NumberInputLabel field={'resource.startCount'} value={props.resource.startCount}
                                              onChange={(value) => {
                                                  props.resource.startCount = value;
                                                  executeCommand(new UpdateResourceCommand(props.resource));
                                              }}/>
                            <NumberInputLabel field={'resource.visionModifier'} value={props.resource.visionModifier}
                                              onChange={(value) => {
                                                  props.resource.visionModifier = value;
                                                  executeCommand(new UpdateResourceCommand(props.resource));
                                              }}/>
                        </div>
                    </div>
                </div>
            </Collapsible>
        </div>
    )
}

export default ResourcesEditor;